import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from './LoadingSpinner';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface AuditLog {
  _id: string;
  actor: string;
  action: string;
  targetType: string;
  targetId?: string;
  targetEmail?: string;
  changes: any;
  metadata: any;
  ipAddress?: string;
  timestamp: string;
}

interface ActionStat {
  _id: string;
  count: number;
}

interface AuditLogResponse {
  logs: AuditLog[];
  stats: ActionStat[];
  total: number;
  filters: any;
}

const AuditLogViewer: React.FC = () => {
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [searchEmail, setSearchEmail] = useState('');
  const [limit, setLimit] = useState(50);

  // Fetch audit logs
  const { data, isLoading, error, refetch } = useQuery<AuditLogResponse>({
    queryKey: ['audit-logs', actionFilter, searchEmail, limit],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token');

      const params = new URLSearchParams();
      if (actionFilter !== 'all') params.append('action', actionFilter);
      if (searchEmail) params.append('targetEmail', searchEmail);
      params.append('limit', limit.toString());

      const response = await fetch(`${API_BASE}/api/admin-frontend/audit-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      'user.suspend': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'user.activate': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'user.role.update': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'waitlist.approve_beta': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'config.update': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    };

    const color = colors[action] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>
        {action}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <p className="text-red-800 dark:text-red-200">
          Failed to load audit logs: {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Audit Logs
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Track all administrative actions and changes
        </p>
      </div>

      {/* Stats Cards */}
      {data?.stats && data.stats.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Action Summary (All Time)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.stats.slice(0, 8).map((stat) => {
              const actionLabels: Record<string, string> = {
                'user.suspend': 'Suspensions',
                'user.activate': 'Activations',
                'user.role.update': 'Role Changes',
                'waitlist.approve_beta': 'Beta Approvals',
                'waitlist.config.update': 'Config Updates',
                'waitlist.remove': 'Waitlist Removals',
              };

              const actionIcons: Record<string, string> = {
                'user.suspend': '🚫',
                'user.activate': '✅',
                'user.role.update': '👑',
                'waitlist.approve_beta': '🎯',
                'waitlist.config.update': '⚙️',
                'waitlist.remove': '🗑️',
              };

              return (
                <div key={stat._id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{actionIcons[stat._id] || '📝'}</span>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stat.count}
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {actionLabels[stat._id] || stat._id}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by email..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Actions</option>
              <option value="user.suspend">Suspensions</option>
              <option value="user.activate">Activations</option>
              <option value="user.role.update">Role Changes</option>
              <option value="waitlist.approve_beta">Beta Approvals</option>
            </select>
          </div>
          <div>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="25">25 logs</option>
              <option value="50">50 logs</option>
              <option value="100">100 logs</option>
              <option value="200">200 logs</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Activity
            </h3>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              Refresh
            </button>
          </div>

          {isLoading ? (
            <div className="py-12 text-center">
              <LoadingSpinner size="lg" text="Loading audit logs..." />
            </div>
          ) : !data?.logs || data.logs.length === 0 ? (
            <div className="py-12 text-center text-gray-500 dark:text-gray-400">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p>No audit logs found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.logs.map((log) => (
                <div
                  key={log._id}
                  className="flex gap-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {getActionBadge(log.action)}
                        {log.targetEmail && (
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            → {log.targetEmail}
                          </span>
                        )}
                      </div>
                      <time className="text-xs text-gray-500 dark:text-gray-500 flex-shrink-0">
                        {formatTimestamp(log.timestamp)}
                      </time>
                    </div>

                    <div className="text-sm text-gray-900 dark:text-white mb-1">
                      <span className="font-semibold">{log.actor}</span>
                      {' '}
                      {log.action.includes('suspend') && 'suspended'}
                      {log.action.includes('activate') && 'activated'}
                      {log.action.includes('role') && 'changed role for'}
                      {log.action.includes('approve') && 'approved'}
                      {' '}
                      {log.targetType === 'user' && 'user'}
                      {log.targetType === 'waitlist' && 'beta access'}
                      {log.targetType === 'config' && 'configuration'}
                    </div>

                    {/* Changes */}
                    {log.changes && Object.keys(log.changes).length > 0 && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        {log.changes.before && log.changes.after && (
                          <div className="flex gap-2">
                            <span className="text-red-600 dark:text-red-400">
                              {JSON.stringify(log.changes.before)}
                            </span>
                            <span>→</span>
                            <span className="text-green-600 dark:text-green-400">
                              {JSON.stringify(log.changes.after)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Metadata (reason) */}
                    {log.metadata?.reason && (
                      <div className="text-xs text-gray-500 dark:text-gray-500 italic mt-1">
                        Reason: {log.metadata.reason}
                      </div>
                    )}

                    {/* IP Address */}
                    {log.ipAddress && (
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        IP: {log.ipAddress}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {data?.logs && data.logs.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {data.logs.length} audit log{data.logs.length !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
